import gql from 'graphql-tag';
import { useQuery } from '@apollo/react-hooks';

import { isUnauthorizedError, Attribution } from '@confluence/error-boundary';
import { containsNoNetworkError } from '@confluence/network';
import { getMonitoringClient } from '@confluence/monitoring';
import { markErrorAsHandled } from '@confluence/graphql';
import { getAutoConvertionInfo } from '@confluence/editor-conversion';
import type { ShareContentType } from '@confluence/share';
import { fg } from '@confluence/feature-gating';

type UseIsPageAutoConvertedType = {
	contentId: string;
	contentType: ShareContentType;
};

// used only for determining whether the "revert to legacy editor" menu item should show in overflow menu of view/edit page and blogposts
export const useIsPageOrBlogAutoConverted = ({
	contentId,
	contentType,
}: UseIsPageAutoConvertedType): boolean => {
	const { data: contentPropertiesData, error } = useQuery(
		gql`
			query useIsPageOrBlogAutoConvertedQuery($contentId: ID, $versionOverride: Int) {
				content(id: $contentId, version: $versionOverride) {
					nodes {
						id
						type
						properties(key: "editor") {
							nodes {
								id
								key
								value
							}
						}
						operations {
							operation
							targetType
						}
						body {
							dynamic {
								representation
							}
						}
						metadata {
							frontend {
								fabricEditorSupported
							}
						}
					}
				}
			}
		`,
		{
			variables: {
				contentId,
			},
			// skip if is not page or blogpost as the revert to legacy editor menu item can only show for those content types
			skip:
				!(contentType === 'page' || contentType === 'blogpost') &&
				fg('confluence_frontend_reintroduce_revert_editor_item'),
		},
	);

	if (!fg('confluence_frontend_reintroduce_revert_editor_item')) {
		return false;
	}

	if (error) {
		if (isUnauthorizedError(error) || containsNoNetworkError(error)) {
			markErrorAsHandled(error);
		} else {
			getMonitoringClient().submitError(error, {
				attribution: Attribution.BACKBONE,
			});
		}
		return false;
	}

	if (contentPropertiesData) {
		const { isAutoConverted } = getAutoConvertionInfo(contentPropertiesData?.content?.nodes?.[0]);
		const isNewPage = contentPropertiesData?.content?.nodes?.length === 0;

		return !!isAutoConverted && !isNewPage;
	}

	return false;
};
